import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NewPageWrapper, RichTable, useTitle } from '../../common';
import { useGetExternalApps } from './hooks/useGetExternalApps';
import { FuseOptions } from 'fuse.js';
import { ExternalAppDto } from '../../api-client';
import { useSearch } from '../../common/hooks/useSearch/useSearch';
import {
  TableData,
  TableProps,
} from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';
import { ErrorResponse } from '../../common/types/errorRespose';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { CopyToClipboard } from '../../common/components/CopyToClipboard/CopyToClipboard';
import { RowActions } from '../../common/components/RowActions/RowActions';
import { useUpdateExternalApp } from './hooks/useUpdateExternalApp';
import { NotificationsContext } from '../../common/notifications/Notifications';
import { useQueryClient } from 'react-query';
import { FormattedDate } from 'react-intl';
import { UpdateExternalAppModal } from './ui/modals/UpdateExternalAppModal/UpdateExternalAppModal';
import { DeleteConfirmationModal } from '../../common/components/DeleteConfirmationModal/DeleteConfirmationModal';
import { useDeleteCustomRole } from './hooks/useDeleteExternalApp';
import { CreateExternalAppModal } from './ui/modals/CreateExternalAppModal/CreateExternalAppModal';
import { useCreateExternalApp } from './hooks/useCreateExternalApp';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { CreateButton } from '../../common/components/Breadcrumbs/buttons/CreateButton/CreateButton';

export const ExternalApps: React.FC = () => {
  useTitle('External Apps');
  const { updateActions } = useBreadcrumbsActions();
  const { showNotification } = useContext(NotificationsContext);
  const queryClient = useQueryClient();
  useEffect(() => {
    updateActions(
      <>
        <RefreshButton onClick={refetchExternalApps} permission="iam.external-app.list" />
        <CreateButton
          title="New external app"
          onClick={toggleCreateModalOpen}
          permission="iam.external-app.create"
        />
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const toggleUpdateDialogOpen = () => {
    setIsUpdateModalOpen((prev) => !prev);
  };
  const [externalAppToUpdate, setExternalAppToUpdate] = useState<ExternalAppDto | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const toggleDeleteDialogOpen = () => {
    setIsDeleteModalOpen((prev) => !prev);
  };
  const [externalAppToDelete, setExternalAppToDelete] = useState<ExternalAppDto | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const toggleCreateModalOpen = () => {
    setIsCreateModalOpen((prev) => !prev);
  };

  const {
    data: externalApps,
    isFetching: isFetchingExternalApps,
    error: fetchError,
    refetch: refetchExternalApps,
  } = useGetExternalApps();

  const {
    mutate: updateExternalApp,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateExternalApp({
    onSuccess: () => {
      showNotification('External application updated successfully');
      queryClient.invalidateQueries('external-apps').then();
      toggleUpdateDialogOpen();
    },
    onError: () => {
      showNotification('An error occurred when updating external application');
    },
  });

  const {
    mutate: createExternalApp,
    isLoading: isCreating,
    error: createError,
  } = useCreateExternalApp({
    onSuccess: () => {
      showNotification('External application created successfully');
      queryClient.invalidateQueries('external-apps').then();
      toggleCreateModalOpen();
    },
  });

  const { mutate: deleteExternalApp, isLoading: isDeleting } = useDeleteCustomRole({
    onSuccess: () => {
      showNotification('External application deleted successfully');
      queryClient.invalidateQueries('external-apps').then();
      toggleDeleteDialogOpen();
    },
  });

  const searchOptions: FuseOptions<ExternalAppDto> = useMemo(
    () => ({
      keys: ['name', 'description'],
      threshold: 0.1,
      caseSensitive: false,
      findAllMatches: true,
    }),
    [],
  );

  const { filteredData: filteredExternalApps, onSearchChange } = useSearch(
    externalApps as ExternalAppDto[],
    searchOptions,
  );

  const tableColumns: TableProps['columns'] = useMemo(
    () => [
      { Header: 'Name', accessor: 'name', width: 150 },
      {
        Header: 'Description',
        accessor: 'description',
        width: 150,
      },
      {
        Header: 'API Key',
        width: 500,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: ExternalAppDto;
          };
        }) => <CopyToClipboard hideText text={original.apiKey} variant="body2" ellipsis />,
      },
      {
        Header: 'Last used at',
        width: 100,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: ExternalAppDto;
          };
        }) => (original.lastUsedAt ? <FormattedDate value={original.lastUsedAt} /> : 'Never'),
      },
      {
        Header: 'Actions',
        accessor: (row: ExternalAppDto) => `actions-${row.id}`,
        width: 100,
        Cell: ({
          row: { original },
        }: {
          row: {
            original: ExternalAppDto;
          };
        }) => (
          <RowActions
            original={original}
            setItemToUpdate={setExternalAppToUpdate}
            toggleUpdateItemModal={toggleUpdateDialogOpen}
            updatePermission="iam.external-app.update"
            setItemToDelete={setExternalAppToDelete}
            toggleDeleteItemModal={toggleDeleteDialogOpen}
            deletePermission="iam.external-app.delete"
          />
        ),
      },
    ],
    [],
  );

  return (
    <NewPageWrapper>
      {externalAppToDelete && (
        <DeleteConfirmationModal
          title={externalAppToDelete?.name || ''}
          entity="external application"
          isOpen={isDeleteModalOpen}
          onClose={toggleDeleteDialogOpen}
          isDeleting={isDeleting}
          deleteEntity={() => deleteExternalApp(externalAppToDelete.id)}
          dataTestId="delete-external-app-confirmation-modal"
        />
      )}
      {isCreateModalOpen && (
        <CreateExternalAppModal
          onClose={toggleCreateModalOpen}
          isOpen={isCreateModalOpen}
          isCreating={isCreating}
          error={createError}
          createExternalApp={createExternalApp}
        />
      )}
      {externalAppToUpdate && isUpdateModalOpen ? (
        <UpdateExternalAppModal
          updateExternalApp={updateExternalApp}
          isOpen={isUpdateModalOpen}
          isUpdating={isUpdating}
          error={updateError}
          externalApp={externalAppToUpdate}
          onClose={() => setIsUpdateModalOpen(false)}
        />
      ) : null}
      <RichTable
        id="external-apps-table"
        title="Browse external apps"
        columns={tableColumns}
        data={filteredExternalApps as unknown as TableData[]}
        isLoading={isFetchingExternalApps}
        searchFieldLabel="Filter by name or description"
        onSearchChange={onSearchChange}
        fetchError={fetchError as ErrorResponse | null}
        refetch={refetchExternalApps}
        errorSubject="external apps"
        permissions={{
          create: 'iam.external-app.create',
          list: 'iam.external-app.list',
        }}
      />
    </NewPageWrapper>
  );
};
